import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Server,
  Users,
  Settings,
  Logs,
  MessageSquareText,
  FileText,
  Network,
  Store,
} from 'lucide-react';
import { useServerContext } from '@/contexts/ServerContext';
import { useGroupData } from '@/hooks/useGroupData';
import csdcLogo from '@/assets/csdc-logo-navbar.svg';

interface SidebarProps {
  collapsed: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const { t } = useTranslation();
  const { allServers } = useServerContext();
  const { groups } = useGroupData();

  const workspaceItems = [
    { path: '/', icon: <LayoutDashboard size={20} />, label: t('nav.dashboard') },
    { path: '/servers', icon: <Server size={20} />, label: t('nav.servers'), badge: allServers.length },
    { path: '/groups', icon: <Network size={20} />, label: t('nav.groups'), badge: groups.length },
    { path: '/prompts', icon: <MessageSquareText size={20} />, label: t('nav.prompts') },
    { path: '/resources', icon: <FileText size={20} />, label: t('nav.resources') },
    { path: '/market', icon: <Store size={20} />, label: t('nav.market') },
  ];

  const systemItems = [
    { path: '/users', icon: <Users size={20} />, label: t('nav.users') },
    { path: '/logs', icon: <Logs size={20} />, label: t('nav.logs') },
    { path: '/settings', icon: <Settings size={20} />, label: t('nav.settings') },
  ];

  const renderItem = (item: (typeof workspaceItems)[0]) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        'flex items-center gap-2.5 py-1.5 rounded-[10px] transition-colors ' + (collapsed ? 'justify-center px-0' : 'px-2') +
        (isActive
          ? ' bg-[#FFE600]'
          : ' hover:bg-black/[0.03] dark:hover:bg-white/[0.04]')
      }
    >
      {({ isActive }) => (
        <>
          <span
            className="flex-shrink-0 text-[#6B7280]"
          >
            {item.icon}
          </span>
          {!collapsed && (
            <>
              <span className="truncate text-[15px]" style={{ fontWeight: isActive ? 600 : 500 }}>
                {item.label}
              </span>
              {(item as any).badge != null && (
                <span className="ml-auto text-xs text-[#6E6E73] shrink-0">
                  {(item as any).badge}
                </span>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <aside
      className={
        'flex flex-col h-full relative shrink-0 transition-[width] duration-200 ease-out ' +
        'bg-[#FFF] border-r border-[#D2D2D7] ' +
        (collapsed ? 'w-14' : 'w-[220px]')
      }
    >
      {collapsed && (
        <div className="flex justify-center py-3">
          <img src={csdcLogo} alt="CSDC" className="h-7 w-9 object-contain" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto pb-2">
        {!collapsed && <div className="hub-sect px-[14px] pt-4 pb-3">{t('nav.workspace')}</div>}
        <nav className={'flex flex-col gap-0.5 ' + (collapsed ? 'px-1.5' : 'px-2')}>
          {workspaceItems.map(renderItem)}
        </nav>

        {!collapsed && <div className="hub-sect px-[14px] pt-5 pb-3">{t('nav.system')}</div>}
        <nav className={'flex flex-col gap-0.5 ' + (collapsed ? 'px-1.5 mt-1' : 'px-2')}>
          {systemItems.map(renderItem)}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
